import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import { Animated, View as NativeView } from "react-native";
import { View } from "@lookiero/aurora";
import { Text } from "@lookiero/sty-psp-ui";
import { ProductPreviewWithDiscount } from "../../../../domain/projections/ProductPreviewWithDiscount";
import { useExtendedScreen } from "../../hooks/useExtendedScreen";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import { useRegisterElementForInView } from "../../hooks/useInView";
import { financial } from "../../shared/util/financial/financial";
import { TouchableWithoutFeedback } from "../touchableWithoutFeedback/TouchableWithoutFeedback";
import { style as productPreviewCardStyle } from "./ProductPreviewCard.style";

interface ProductPreviewCardProps {
  readonly product: ProductPreviewWithDiscount;
  readonly imageUrl: string;
  readonly detailImageUrl: string;
  readonly onItemPress: (product: ProductPreviewWithDiscount) => void;
}

const ProductPreviewCard: FC<ProductPreviewCardProps> = ({ product, imageUrl, detailImageUrl, onItemPress }) => {
  const formatCurrency = useFormatCurrency();

  const style = useMemo(() => productPreviewCardStyle(), []);

  const screen = useExtendedScreen();
  const isDesktop = screen.XL;

  /**
   * TODO
   * We are asuming that this component will be always rendered under InViewProvider.
   * We should decouple it to make it independent.
   * https://lookiero.atlassian.net/browse/LK-31578
   */
  const registerElementForInView = useRegisterElementForInView();

  const animationConfig = { duration: 300, useNativeDriver: true };

  const regularImageOpacity = useRef(new Animated.Value(0)).current;
  const regularImagefadeIn = Animated.timing(regularImageOpacity, {
    ...animationConfig,
    toValue: 1,
  });

  const detailImageOpacity = useRef(new Animated.Value(0)).current;
  const detailImageFadeIn = Animated.timing(detailImageOpacity, {
    ...animationConfig,
    toValue: 1,
  });
  const detailImageFadeOut = Animated.timing(detailImageOpacity, {
    ...animationConfig,
    toValue: 0,
  });

  const handleHoverIn = useCallback(() => {
    if (isDesktop === false) {
      return;
    }

    detailImageFadeIn.start();
  }, [detailImageFadeIn, isDesktop]);

  const handleHoverOut = useCallback(() => {
    if (isDesktop === false) {
      return;
    }

    detailImageFadeOut.start();
  }, [detailImageFadeOut, isDesktop]);

  const hasPercentageDiscount = product.price.discountedPrice && product.price.discountedPrice.percentage > 0;

  const [imageLoaded, setImageLoaded] = useState(false);
  const onEnterViewport = useCallback(
    (inViewPort: boolean) => {
      if (inViewPort === false || imageLoaded === true) {
        return;
      }
      setImageLoaded(true);
    },
    [imageLoaded],
  );

  return (
    <NativeView
      key={`${product.id}`}
      ref={(itemRef) => {
        registerElementForInView(product.id, itemRef, onEnterViewport);
      }}
      testID={`product-preview-card-${product.id}`}
    >
      <View style={style.imageWrapper}>
        <TouchableWithoutFeedback
          style={[style.imageCta, isDesktop ? style.imageCtaLarge : undefined]}
          testID="product-preview-image-container"
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          onPress={() => onItemPress(product)}
        >
          <View style={[style.image, style.placeHolder]}>
            {imageLoaded ? (
              <>
                <Animated.Image
                  resizeMode="cover"
                  source={{ uri: imageUrl }}
                  style={{ ...style.image, opacity: regularImageOpacity }}
                  onLoad={() => regularImagefadeIn.start()}
                />
                <Animated.Image
                  resizeMode="cover"
                  source={{ uri: isDesktop ? detailImageUrl : "" }}
                  style={{ ...style.detailImage, opacity: detailImageOpacity }}
                />
              </>
            ) : (
              <></>
            )}
          </View>
        </TouchableWithoutFeedback>
      </View>
      <View style={isDesktop ? undefined : style.footerInfoSmall}>
        <Text ellipsizeMode="tail" level={2} style={style.brand} variant="detail">
          {product.brand}
        </Text>
        <Text ellipsizeMode="tail" level={2} variant="detail">
          {product.title}
        </Text>
        <View style={style.pricesWrapper}>
          <Text level={2} variant="detail">
            {formatCurrency({
              amount: financial(hasPercentageDiscount ? product.price.discountedPrice.amount : product.price.amount),
            })}
          </Text>
          {hasPercentageDiscount ? (
            <Text level={2} style={style.previousPrice} variant="detail">
              {formatCurrency({ amount: financial(product.price.amount) })}
            </Text>
          ) : (
            <></>
          )}
        </View>
        {hasPercentageDiscount ? (
          <View style={style.discountWrapper}>
            <Text level={2} style={style.discount} variant="detail">
              -{product.price.discountedPrice.percentage}%
            </Text>
          </View>
        ) : (
          <></>
        )}
      </View>
    </NativeView>
  );
};

export { ProductPreviewCard };
